import { skipToken } from '@reduxjs/toolkit/query'
import { Box, Button, Flex, HStack, Icon, Text, VStack, useToast } from '@chakra-ui/react'
import { useState } from 'react'
import { FaMapMarkerAlt } from 'react-icons/fa'
import { useAppSelector } from '../../../app/typedReduxHooks'
import {
  useGetAvailablePrescriberLocationProfilesQuery,
  useGetPrescriberLocationProfilesQuery,
  useLeavePrescriberLocationProfileMutation,
} from '../prescriberLocationProfilesSlice'
import { JoinLocationModal } from './JoinLocationModal'
import { EditLocationProfileModal } from './EditLocationProfileModal'
import { JoinedLocationProfileCard } from './JoinedLocationProfileCard'
import type { PrescriberLocationProfile } from '../../../types/location'

export const LocationProfilesList = () => {
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const { data: locationProfiles = [], isLoading: isLoadingProfiles } =
    useGetPrescriberLocationProfilesQuery()
  const { data: availableJoinLocations = [] } = useGetAvailablePrescriberLocationProfilesQuery(
    prescriber?.id ? undefined : skipToken
  )
  const [leaveLocationProfile, { isLoading: isLeaving }] =
    useLeavePrescriberLocationProfileMutation()
  const toast = useToast()

  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState<PrescriberLocationProfile | null>(null)
  const [leavingProfileId, setLeavingProfileId] = useState<string | null>(null)

  const handleLeaveConfirm = async (locationProfileId: string) => {
    try {
      await leaveLocationProfile({ locationProfileId }).unwrap()
      toast({
        title: 'Successfully left location',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to leave location',
        description: error?.data?.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    } finally {
      setLeavingProfileId(null)
    }
  }

  if (isLoadingProfiles) return null

  return (
    <Box w="full" mt={8}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Icon as={FaMapMarkerAlt} color="red.500" boxSize={4} />
          <Text fontWeight="bold" fontSize="lg" color="blue.800">
            Location Profiles
          </Text>
          <Text fontSize="sm" color="gray.500">
            Contact details and provider number per location
          </Text>
        </HStack>
        <Button
          colorScheme="blue"
          size="sm"
          onClick={() => setIsJoinModalOpen(true)}
          isDisabled={availableJoinLocations.length === 0}
        >
          + Join location
        </Button>
      </Flex>

      <VStack spacing={4} align="stretch">
        {locationProfiles
          .filter((profile) => profile.joined)
          .map((profile) => {
            return (
              <JoinedLocationProfileCard
                key={profile.id}
                profile={profile}
                isConfirmingLeave={leavingProfileId === profile.id}
                isLeaveSubmitting={isLeaving}
                onEdit={() => setEditingProfile(profile)}
                onRequestLeave={() => setLeavingProfileId(profile.id)}
                onConfirmLeave={() => handleLeaveConfirm(profile.id)}
                onCancelLeave={() => setLeavingProfileId(null)}
              />
            )
          })}
      </VStack>

      <JoinLocationModal isOpen={isJoinModalOpen} onClose={() => setIsJoinModalOpen(false)} />

      {editingProfile ? (
        <EditLocationProfileModal
          isOpen
          onClose={() => setEditingProfile(null)}
          profile={editingProfile}
          locationName={editingProfile.name || 'Unknown location'}
        />
      ) : null}
    </Box>
  )
}
